import { UserX, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useConnectedAccount } from '@/providers/connectedAccount'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/ui-components/Dialog'
import { Field, Label } from '@/ui-components/Fieldset'
import { Listbox, ListboxOption } from '@/ui-components/Listbox'
import type { ConnectedAccountUser, OrganizationUser } from '@/views/Users/types'

export type RemoveUserFromConnectedAccountsModalProps = {
  isOpen: boolean
  onClose: () => void
  user: OrganizationUser | ConnectedAccountUser | null
  connectedAccountIds: string[]
  onSubmit: (userId: string, vesselIds: string[]) => void
}

const RemoveUserFromConnectedAccountsModal = ({
  isOpen,
  onClose,
  user,
  connectedAccountIds,
  onSubmit,
}: RemoveUserFromConnectedAccountsModalProps) => {
  const { connectedAccounts } = useConnectedAccount()
  const [isRemoving, setIsRemoving] = useState(false)
  const [selectedConnectedAccountIds, setSelectedConnectedAccountIds] = useState<string[]>([])

  useEffect(() => {
    setIsRemoving(false)
  }, [isOpen])

  // If there is only one connected account, select it by default
  useEffect(() => {
    if (connectedAccountIds.length === 1) {
      setSelectedConnectedAccountIds([connectedAccountIds[0]])
    }
  }, [connectedAccountIds])

  const handleSubmit = () => {
    if (!user || selectedConnectedAccountIds.length === 0) {
      return
    }

    setIsRemoving(true)
    onSubmit(user.id, selectedConnectedAccountIds)
    handleClose()
  }

  const handleClose = () => {
    setIsRemoving(false)
    setSelectedConnectedAccountIds([])
    onClose()
  }

  if (!user) {
    return null
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <UserX className="size-5 text-gray-900 dark:text-white" />
          <DialogTitle>Remove Vessel Access</DialogTitle>
        </div>
        <Button
          plain
          onClick={handleClose}
        >
          <X data-slot="icon" />
        </Button>
      </div>

      <DialogBody>
        <div className="space-y-4">
          <div className="space-y-3">
            <p className="text-base/6 font-semibold sm:text-sm/6 text-gray-900 dark:text-white">User</p>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="font-medium">{user.name}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{user.email}</div>
            </div>
          </div>

          <Field>
            <Label>Vessels</Label>
            <Listbox
              multiple
              value={selectedConnectedAccountIds}
              onChange={setSelectedConnectedAccountIds}
              className="w-full"
              placeholder="Select vessels"
            >
              {connectedAccountIds?.map((connectedAccountId) => (
                <ListboxOption
                  key={connectedAccountId}
                  value={connectedAccountId}
                  data-testid={`connected-account-option-${connectedAccountId}`}
                >
                  {connectedAccounts.find((account) => account.id === connectedAccountId)?.name}
                </ListboxOption>
              ))}
            </Listbox>
          </Field>
        </div>
      </DialogBody>

      <DialogActions>
        <Button
          plain
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          color="red"
          className="data-disabled:opacity-50 data-disabled:cursor-not-allowed"
          disabled={isRemoving}
          onClick={handleSubmit}
          data-testid="remove-user-from-connected-accounts-confirm"
        >
          {isRemoving ? 'Removing...' : 'Remove Vessel Access'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RemoveUserFromConnectedAccountsModal
